import { useMemo } from 'react';
import { Box, Paper, Typography, Chip, Grid } from '@mui/material';
import {
  StoreOutlined,
  AccessTimeOutlined,
  PeopleOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  CalendarTodayOutlined,
} from '@mui/icons-material';
import { Employee } from '../../types/employee.types';
import { Shop } from '../../types/shop.types';
import { Schedule } from '../../types/schedule.types';
import { calculateShopPerformance } from '../../utils/dashboardCalculations';

type ViewMode = 'daily' | 'weekly';

interface ShopPerformanceCardProps {
  rawData: {
    employees: Employee[];
    shops: Shop[];
    schedules: Schedule[];
  };
  viewMode: ViewMode;
  dateRange: string[];
}

export default function ShopPerformanceCard({ rawData, viewMode, dateRange }: ShopPerformanceCardProps) {
  const shopPerformance = useMemo(
    () => calculateShopPerformance(rawData.employees, rawData.shops, rawData.schedules, dateRange),
    [rawData.employees, rawData.shops, rawData.schedules, dateRange]
  );

  if (shopPerformance.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <StoreOutlined color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Shop Performance
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {shopPerformance.map((shop) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={shop.shopId}>
            <Paper
              sx={{
                p: 2.5,
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: `4px solid ${shop.shopColor}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                  borderColor: 'primary.light',
                  borderLeftColor: shop.shopColor,
                },
              }}
            >
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <StoreOutlined sx={{ color: shop.shopColor, flexShrink: 0 }} />
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {shop.shopName}
                  </Typography>
                </Box>
                {viewMode === 'daily' ? (
                  <Chip
                    label={shop.isOpen ? 'Open' : 'Closed'}
                    size="small"
                    color={shop.isOpen ? 'success' : 'default'}
                    variant={shop.isOpen ? 'filled' : 'outlined'}
                  />
                ) : (
                  <Chip
                    label={`${shop.daysOpen}/${shop.totalDays} days`}
                    size="small"
                    color={shop.daysOpen === shop.totalDays ? 'success' : 'default'}
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Hours - only for daily view when shop is open */}
              {viewMode === 'daily' && shop.isOpen && shop.openTime && shop.closeTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccessTimeOutlined fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {shop.openTime} - {shop.closeTime}
                  </Typography>
                </Box>
              )}

              {/* Weekly info */}
              {viewMode === 'weekly' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayOutlined fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {shop.daysOpen} day{shop.daysOpen !== 1 ? 's' : ''} open this week
                  </Typography>
                </Box>
              )}

              {/* Stats */}
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PeopleOutlined fontSize="small" color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {shop.staffCount}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {viewMode === 'daily' ? 'Staff Today' : 'Staff Scheduled'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" fontWeight={600} color="info.main">
                    {shop.totalHours}h
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {viewMode === 'daily' ? 'Hours' : 'Total Hours'}
                  </Typography>
                </Box>
              </Box>

              {/* Coverage Status */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {shop.coverageMet ? (
                  <>
                    <CheckCircleOutlined fontSize="small" color="success" />
                    <Typography variant="body2" color="success.main">
                      Coverage met
                    </Typography>
                  </>
                ) : (
                  <>
                    <CancelOutlined fontSize="small" color="error" />
                    <Typography variant="body2" color="error.main">
                      Needs coverage
                    </Typography>
                  </>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {shop.assignedEmployees} assigned
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
